<template>
  <ConfirmModal
    :title="t('FestivalShop.orders.summary.edit_address')"
    :disabled="!isFormValid || isSubmitting"
    wrapper-class="w-full max-w-2xl h-[80vh] rounded-lg"
    persist
    @close-dialog="handleCloseDialog"
  >
    <template #content>
      <div class="px-6 pb-6">
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <SelectInput
              v-model="formData.salutation"
              name="salutation"
              :label="t('FestivalShop.checkout.salutation')"
              :error="getValidationErrors(v$.salutation)"
              @change="v$.salutation.$validate"
            >
              <option value=""></option>
              <option v-for="option in salutationOptions" :key="option.enum" :value="option.enum">{{ option.name }}</option>
            </SelectInput>
            <SelectInput v-model="formData.title" name="title" :label="t('FestivalShop.checkout.title')">
              <option value=""></option>
              <option v-for="option in titleOptions" :key="option.value" :value="option.value">
                {{ option.text }}
              </option>
            </SelectInput>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <TextInput
              v-model="formData.firstName"
              name="firstName"
              :label="t('FestivalShop.checkout.firstName')"
              :error="getValidationErrors(v$.firstName)"
              @update:model-value="v$.firstName.$validate"
            />
            <TextInput
              v-model="formData.lastName"
              name="lastName"
              :label="t('FestivalShop.checkout.lastName')"
              :error="getValidationErrors(v$.lastName)"
              @update:model-value="v$.lastName.$validate"
            />
          </div>

          <TextInput
            v-model="formData.email"
            name="email"
            type="email"
            :label="t('FestivalShop.checkout.email')"
            :error="getValidationErrors(v$.email)"
            @update:model-value="v$.email.$validate"
          />

          <SelectInput
            v-model="formData.country"
            name="country"
            :label="t('FestivalShop.checkout.country')"
            :error="getValidationErrors(v$.country)"
            @change="v$.country.$validate"
          >
            <option v-for="{ code, translation } in countries" :key="code" :value="code">{{ translation }}</option>
          </SelectInput>

          <TextInput
            v-model="formData.company"
            name="company"
            :label="t('FestivalShop.checkout.company')"
            :error="getValidationErrors(v$.company)"
            @update:model-value="v$.company.$validate"
          />

          <TextInput
            v-model="formData.vatNumber"
            name="vatNumber"
            :label="t('FestivalShop.checkout.vatNumber') + ' ' + t('FestivalShop.checkout.optional')"
          />

          <TextInput
            v-model="formData.customerReference"
            name="customerReference"
            :label="t('FestivalShop.checkout.orderPoNumber') + ' ' + t('FestivalShop.checkout.optional')"
          />

          <div class="grid grid-cols-3 gap-4">
            <TextInput
              v-model="formData.zip"
              name="zip"
              :label="t('FestivalShop.checkout.zipCode')"
              :error="getValidationErrors(v$.zip)"
              @update:model-value="v$.zip.$validate"
            />
            <div class="col-span-2">
              <TextInput
                v-model="formData.city"
                name="city"
                :label="t('FestivalShop.checkout.city')"
                :error="getValidationErrors(v$.city)"
                @update:model-value="v$.city.$validate"
              />
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="col-span-3">
              <TextInput
                v-model="formData.street"
                name="street"
                :label="t('FestivalShop.checkout.street')"
                :error="getValidationErrors(v$.street)"
                @update:model-value="v$.street.$validate"
              />
            </div>
            <TextInput
              v-model="formData.number"
              name="number"
              :label="t('FestivalShop.checkout.no')"
              :error="getValidationErrors(v$.number)"
              @update:model-value="v$.number.$validate"
            />
          </div>

          <TextInput
            v-model="formData.additionalAddress"
            name="additionalAddress"
            :label="t('FestivalShop.checkout.addressAddition') + ' ' + t('FestivalShop.checkout.optional')"
          />
        </div>
      </div>
    </template>
  </ConfirmModal>
</template>

<script lang="ts" setup>
import { SelectInput, TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ConfirmModal from '@/components/ConfirmModal.vue'
import { useCountriesAndNationalitiesData } from '@/composables/useCountriesAndNationalitiesData'
import { useFormData } from '@/composables/useFormData'
import { useUpdateExtrasOrderMutation, ExtrasOrdersDocument } from '@/gql/myomr'
import type { ExtraAttributes } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import { optionalToNullable } from '@/helpers/optionalToNullable'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, email as emailValidator } from '@/services/validators'
import type { ExtrasOrderNode } from '@/views/FestivalShop'

const { t } = useI18n()
const { salutationOptions, titleOptions } = useFormData()
const { countries } = useCountriesAndNationalitiesData()

interface Props {
  order: ExtrasOrderNode
  eventSlug: string
  kioskId: string
}

const props = defineProps<Props>()

interface FormData {
  salutation: string
  title: string
  firstName: string
  lastName: string
  email: string
  country: string
  company: string
  vatNumber: string
  customerReference: string
  zip: string
  city: string
  street: string
  number: string
  additionalAddress: string
}

interface Emits {
  (e: 'close'): void
}

const emit = defineEmits<Emits>()

const formData = reactive<FormData>({
  salutation: '',
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  company: '',
  vatNumber: '',
  customerReference: '',
  zip: '',
  city: '',
  street: '',
  number: '',
  additionalAddress: '',
})

const applyOrder = (order: ExtrasOrderNode) => {
  formData.salutation = order.salutation || ''
  formData.title = order.title || ''
  formData.firstName = order.firstName || ''
  formData.lastName = order.lastName || ''
  formData.email = order.email || ''
  formData.country = order.country || ''
  formData.company = order.company || ''
  formData.vatNumber = order.vatNumber || ''
  formData.customerReference = order.customerReference || ''
  formData.zip = order.zip || ''
  formData.city = order.city || ''
  formData.street = order.street || ''
  formData.number = order.number || ''
  formData.additionalAddress = order.additionalAddress || ''
}

watch(
  () => props.order,
  (order) => {
    applyOrder(order)
  },
  { immediate: true },
)

const rules = {
  salutation: { required },
  firstName: { required },
  lastName: { required },
  email: { required, emailValidator },
  country: { required },
  company: { required },
  zip: { required },
  city: { required },
  street: { required },
  number: { required },
}

const v$ = useVuelidate(rules, formData)

const isFormValid = computed(() => !v$.value.$invalid)

const { mutate: updateExtrasOrder } = useUpdateExtrasOrderMutation()
const isSubmitting = ref(false)

const buildExtrasPayload = (): ExtraAttributes[] => {
  return props.order.extras.map((extra) => ({
    productId: extra.product.id,
    variantId: extra.variant.id,
    count: extra.count,
    comment: extra.comment || undefined,
  }))
}

const handleCloseDialog = async (isConfirm: boolean) => {
  if (!isConfirm) {
    emit('close')
    return
  }

  const isValid = await v$.value.$validate()
  if (!isValid || !isFormValid.value) return

  isSubmitting.value = true

  try {
    const extrasOrderResult = await updateExtrasOrder(
      {
        kioskId: props.kioskId,
        rootEvent: { slug: props.eventSlug },
        extrasOrderId: props.order.id,
        extras: buildExtrasPayload(),
        invoicingAttributes: {
          salutation: formData.salutation,
          title: optionalToNullable(formData.title),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          country: formData.country,
          company: formData.company,
          vatNumber: optionalToNullable(formData.vatNumber),
          customerReference: optionalToNullable(formData.customerReference),
          zip: formData.zip,
          city: formData.city,
          street: formData.street,
          number: formData.number,
          additionalAddress: optionalToNullable(formData.additionalAddress),
          business: !!formData.company,
        },
      },
      {
        refetchQueries: [
          {
            query: ExtrasOrdersDocument,
            variables: {
              rootEvent: { slug: props.eventSlug },
              kioskFilter: { id: props.kioskId },
            },
          },
        ],
      },
    )

    const extrasOrderErrors = extrasOrderResult?.data?.updateExtrasOrder?.errors || []
    if (extrasOrderErrors.length > 0) {
      showNotification(extrasOrderErrors.join(', '), 'error')
      return
    }

    showNotification(t('FestivalShop.products.details.notification.order_updated'), 'success')
    emit('close')
  } catch (error) {
    const message = error instanceof Error ? error.message : t('FestivalShop.checkout.generalError')
    showNotification(message, 'error')
  } finally {
    isSubmitting.value = false
  }
}
</script>
